const DEFAULT_LANG =
  (typeof window !== "undefined" &&
    window.gtranslateSettings?.default_language) ||
  "en";

/** Language labels used in LanguageSelector */
export const LANGUAGE_CODE_MAP = {
  English: "en",
  "English (UK)": "en",
  Hindi: "hi",
  Gujarati: "gu",
  Marathi: "mr",
};

export function languageLabelToCode(label) {
  return LANGUAGE_CODE_MAP[label] || DEFAULT_LANG;
}

/** Read active target language from googtrans cookie / localStorage */
export function getActiveGoogTransLangCode() {
  if (typeof document === "undefined") return DEFAULT_LANG;

  const cookieMatch = document.cookie.match(/(?:^|;\s*)googtrans=([^;]*)/);
  const cookieVal = cookieMatch?.[1]
    ? decodeURIComponent(cookieMatch[1].trim())
    : "";
  const cookieParts = cookieVal.split("/").filter(Boolean);
  if (cookieParts.length > 0) {
    const lang = cookieParts[cookieParts.length - 1];
    if (lang && lang !== "auto") return lang;
  }

  const stored = localStorage.getItem("googtrans") || "";
  const storedParts = stored.split("/").filter(Boolean);
  if (storedParts.length > 0) {
    const lang = storedParts[storedParts.length - 1];
    if (lang && lang !== "auto") return lang;
  }

  return DEFAULT_LANG;
}

function isIpHostname(hostname) {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":");
}

/** Parent domains that would share googtrans across tenant subdomains. */
function getSharedCookieDomains() {
  if (typeof window === "undefined") return [];
  const hostname = window.location.hostname;
  if (!hostname || hostname === "localhost" || isIpHostname(hostname)) {
    return [];
  }
  const parts = hostname.split(".");
  if (parts.length < 3) return [];
  const parent = parts.slice(-2).join(".");
  return [parent, `.${parent}`];
}

function expireGoogTransCookieOnDomain(domain) {
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  const domainPart = domain ? `domain=${domain}; ` : "";
  document.cookie = `googtrans=; ${domainPart}path=/; expires=${expires}`;
}

function expireSharedGoogTransCookies() {
  getSharedCookieDomains().forEach((domain) => {
    try {
      expireGoogTransCookieOnDomain(domain);
    } catch {
      // Ignore cookie domain errors on local/dev domains.
    }
  });
}

function writeHostGoogTransCookie(googTransValue) {
  document.cookie = `googtrans=${googTransValue}; path=/; max-age=31536000`;
  try {
    document.cookie = `googtrans=${googTransValue}; domain=${window.location.hostname}; path=/; max-age=31536000`;
  } catch {
    // Ignore cookie domain errors on local/dev domains.
  }
}

export function setGoogTransCookie(targetLangCode) {
  const googTransValue = `/${DEFAULT_LANG}/${targetLangCode}`;
  expireSharedGoogTransCookies();
  writeHostGoogTransCookie(googTransValue);
  localStorage.setItem("googtrans", googTransValue);
  return googTransValue;
}

export function clearGoogTransCookie() {
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=; path=/; expires=${expires}`;
  try {
    expireGoogTransCookieOnDomain(window.location.hostname);
    expireGoogTransCookieOnDomain(`.${window.location.hostname}`);
  } catch {
    // ignore
  }
  expireSharedGoogTransCookies();
  localStorage.removeItem("googtrans");
}

/**
 * Drop parent-domain googtrans (shared by jerp/manglik) and restore this
 * origin's language from localStorage so tenants do not leak language.
 */
export function isolateGoogTransCookieToHost() {
  if (typeof window === "undefined") return;
  expireSharedGoogTransCookies();
  const stored = localStorage.getItem("googtrans") || "";
  const storedParts = stored.split("/").filter(Boolean);
  const lang = storedParts[storedParts.length - 1];
  if (lang && lang !== "auto" && lang !== DEFAULT_LANG) {
    writeHostGoogTransCookie(stored);
  }
}

function getCurrentLangFromCookie() {
  const keyValue = document.cookie.match(/(^|;) ?googtrans=([^;]*)(;|$)/);
  return keyValue ? keyValue[2].split("/")[2] : null;
}

function fireComboChangeEvent(element, eventName) {
  try {
    if (document.createEventObject) {
      const evt = document.createEventObject();
      element.fireEvent(`on${eventName}`, evt);
    } else {
      const evt = document.createEvent("HTMLEvents");
      evt.initEvent(eventName, true, true);
      element.dispatchEvent(evt);
    }
  } catch {
    // ignore
  }
}

function getGoogleTranslateCombo() {
  const selects = document.getElementsByTagName("select");
  for (let i = 0; i < selects.length; i += 1) {
    if (selects[i].className?.includes("goog-te-combo")) {
      return selects[i];
    }
  }
  return null;
}

/**
 * Same logic as GTranslate float.js doGTranslate — schedules retries until the
 * combo exists, then fires change (translateHtml).
 */
function installDoGTranslate() {
  if (typeof window.doGTranslate === "function") return;

  window.doGTranslate = function doGTranslate(langPair) {
    let pair = langPair;
    if (pair?.value) pair = pair.value;
    if (!pair) return;

    const targetLang = pair.split("|")[1];
    const sourceLang = pair.split("|")[0];
    if (
      getCurrentLangFromCookie() == null &&
      targetLang === sourceLang
    ) {
      return;
    }

    let teCombo;
    const selects = document.getElementsByTagName("select");
    for (let i = 0; i < selects.length; i += 1) {
      if (selects[i].className.indexOf("goog-te-combo") !== -1) {
        teCombo = selects[i];
        break;
      }
    }

    const host = document.getElementById("google_translate_element2");
    if (
      host == null ||
      host.innerHTML.length === 0 ||
      !teCombo ||
      teCombo.length === 0 ||
      teCombo.innerHTML.length === 0
    ) {
      window.setTimeout(() => window.doGTranslate(pair), 500);
      return;
    }

    teCombo.value = targetLang;
    fireComboChangeEvent(teCombo, "change");
    fireComboChangeEvent(teCombo, "change");
  };
}

function installGoogleTranslateElementInit() {
  if (typeof window.googleTranslateElementInit2 === "function") return;

  window.googleTranslateElementInit2 = function googleTranslateElementInit2() {
    if (!window.google?.translate?.TranslateElement) return;
    new window.google.translate.TranslateElement(
      { pageLanguage: DEFAULT_LANG, autoDisplay: false },
      "google_translate_element2",
    );
  };
}

function ensureTranslateHostElement() {
  if (document.getElementById("google_translate_element2")) return;

  const wrapper = document.querySelector(".gtranslate_wrapper");
  if (!wrapper) return;

  const host = document.createElement("div");
  host.id = "google_translate_element2";
  wrapper.appendChild(host);
}

function waitForDoGTranslate(maxMs = 15000) {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (typeof window.doGTranslate === "function") {
        resolve(true);
        return;
      }
      if (Date.now() - started >= maxMs) {
        installDoGTranslate();
        resolve(typeof window.doGTranslate === "function");
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

export function isGTranslateEngineReady() {
  const host = document.getElementById("google_translate_element2");
  const combo = getGoogleTranslateCombo();
  return Boolean(
    host &&
      host.innerHTML.length > 0 &&
      combo &&
      combo.options &&
      combo.options.length > 0,
  );
}

let loadLibraryPromise = null;

/**
 * GTranslate float.js only loads translate.google.com on pointerenter when the
 * default language is English — the float switcher is CSS-hidden, so that never
 * runs. Load the library explicitly once float.js (or our fallback) is ready.
 */
export function loadGTranslateLibrary() {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (isGTranslateEngineReady()) {
    return Promise.resolve(true);
  }

  if (loadLibraryPromise) {
    return loadLibraryPromise;
  }

  loadLibraryPromise = (async () => {
    await waitForDoGTranslate();
    installDoGTranslate();
    ensureTranslateHostElement();
    installGoogleTranslateElementInit();

    if (!window.gt_translate_script) {
      const script = document.createElement("script");
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit2";
      script.async = true;
      document.body.appendChild(script);
      window.gt_translate_script = script;
    }

    const deadline = Date.now() + 20000;
    while (Date.now() < deadline) {
      if (isGTranslateEngineReady()) {
        return true;
      }
      await new Promise((r) => window.setTimeout(r, 250));
    }
    return isGTranslateEngineReady();
  })().finally(() => {
    loadLibraryPromise = null;
  });

  return loadLibraryPromise;
}

/** Same lang pair format as GTranslate float switcher: en|target or source|en */
export function buildLangPair(targetLangCode, sourceLangCode) {
  const source = sourceLangCode || getActiveGoogTransLangCode() || DEFAULT_LANG;
  const target = targetLangCode || DEFAULT_LANG;

  if (source === target) return null;

  if (target === DEFAULT_LANG) {
    return `${source}|${DEFAULT_LANG}`;
  }

  return `${DEFAULT_LANG}|${target}`;
}

export function invokeDoGTranslate(langPair) {
  if (!langPair) return false;
  installDoGTranslate();
  if (typeof window.doGTranslate !== "function") {
    return false;
  }

  try {
    window.doGTranslate(langPair);
    return true;
  } catch {
    return false;
  }
}

async function waitForComboTarget(targetLangCode, timeoutMs = 15000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const combo = getGoogleTranslateCombo();
    if (combo && combo.value === targetLangCode) {
      return true;
    }
    await new Promise((r) => window.setTimeout(r, 200));
  }
  return false;
}

export async function applyPageTranslation(targetLangCode) {
  const sourceBeforeCookie = getActiveGoogTransLangCode();
  const pair = buildLangPair(targetLangCode, sourceBeforeCookie);

  if (!pair) {
    if (targetLangCode === DEFAULT_LANG) {
      clearGoogTransCookie();
    }
    return true;
  }

  await loadGTranslateLibrary();
  installDoGTranslate();

  if (!invokeDoGTranslate(pair)) {
    return false;
  }

  const comboReady = await waitForComboTarget(targetLangCode);
  if (!comboReady) {
    return false;
  }

  if (targetLangCode === DEFAULT_LANG) {
    clearGoogTransCookie();
  } else {
    setGoogTransCookie(targetLangCode);
  }

  window.setTimeout(() => {
    invokeDoGTranslate(pair);
    if (targetLangCode === DEFAULT_LANG) {
      clearGoogTransCookie();
    } else {
      setGoogTransCookie(targetLangCode);
    }
  }, 500);

  return true;
}

export async function waitForPageTranslation(
  targetLangCode,
  { maxAttempts = 3, intervalMs = 400 } = {},
) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const ok = await applyPageTranslation(targetLangCode);
    if (ok) return true;
    await new Promise((r) => window.setTimeout(r, intervalMs));
  }
  return false;
}

/** Preload after GTranslate float widget has registered doGTranslate */
export function preloadGTranslate() {
  if (typeof window === "undefined") return;

  isolateGoogTransCookieToHost();

  const start = () => {
    isolateGoogTransCookieToHost();
    loadGTranslateLibrary()
      .catch(() => {})
      .finally(() => {
        isolateGoogTransCookieToHost();
        window.setTimeout(isolateGoogTransCookieToHost, 800);
      });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}
